/**
 * RouteReplay - Animated travel progression replay on the world map
 * Pro-only feature with timeline controls
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { 
  Play, Pause, SkipBack, SkipForward, Calendar, 
  Globe, Trophy, MapPin, Lock, Sparkles 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

const RouteReplay = ({ onClose, onHighlightCountry }) => {
  const { hasUniversalAccess, hasPaidAccess } = useEntitlements();
  const hasAccess = hasUniversalAccess || hasPaidAccess;
  
  const [loading, setLoading] = useState(true);
  const [replayData, setReplayData] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [mode, setMode] = useState("all_time");
  const [selectedYear, setSelectedYear] = useState(null);
  
  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const playIntervalRef = useRef(null);
  
  // Fetch replay data
  const fetchReplayData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ mode });
      if (mode === "yearly" && selectedYear) {
        params.append("year", selectedYear);
      }
      
      const response = await axios.get(`${API}/premium/replay?${params}`);
      setReplayData(response.data);
      setCurrentIndex(0);
      setIsPlaying(false);
    } catch (error) {
      console.error("Failed to fetch replay data:", error);
      if (error.response?.status === 403) {
        // silently show locked state — paywall opens on button tap only
      } else {
        toast.error("Failed to load route replay");
      }
    } finally {
      setLoading(false);
    }
  }, [mode, selectedYear, hasAccess]);
  
  useEffect(() => {
    fetchReplayData();
  }, [fetchReplayData]);
  // Playback controls
  useEffect(() => {
    if (isPlaying && replayData?.timeline?.length > 0) {
      playIntervalRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          const next = prev + 1;
          if (next >= replayData.timeline.length) {
            setIsPlaying(false);
            return prev;
          }
          return next;
        });
      }, 1500); // 1.5 seconds per step
    }
    
    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
      }
    };
  }, [isPlaying, replayData]);
  
  // Highlight current country on map
  useEffect(() => {
    if (replayData?.timeline && currentIndex < replayData.timeline.length) {
      const currentItem = replayData.timeline[currentIndex];
      if (currentItem && onHighlightCountry) {
        onHighlightCountry(currentItem.country_code, currentItem);
      }
    }
  }, [currentIndex, replayData, onHighlightCountry]);
  
  const handlePlay = () => {
    if (!hasAccess) {
      setShowPaywall(true);
      return;
    }
    setIsPlaying(true);
  };
  
  const handlePause = () => {
    setIsPlaying(false);
  };
  
  const handleSeek = (value) => {
    if (!hasAccess) {
      setShowPaywall(true);
      return;
    }
    setCurrentIndex(value[0]);
    setIsPlaying(false);
  };
  
  const handleSkipBack = () => {
    setCurrentIndex(0);
    setIsPlaying(false);
  };
  
  const handleSkipForward = () => {
    if (replayData?.timeline) {
      setCurrentIndex(replayData.timeline.length - 1);
      setIsPlaying(false);
    }
  };
  
  // Get available years for yearly mode
  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => currentYear - i);
  };
  
  const currentItem = replayData?.timeline?.[currentIndex];
  
  if (loading) {
    return (
      <Card className="bg-charcoal/95 border-coral/20 backdrop-blur-xl">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card className="bg-charcoal/95 border-coral/20 backdrop-blur-xl">
        <CardContent className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-coral" />
              <h3 className="font-semibold text-white">Route Replay</h3>
              {!hasAccess && (
                <Badge variant="outline" className="border-coral/50 text-coral text-xs">
                  <Lock className="w-3 h-3 mr-1" />
                  Pro
                </Badge>
              )}
            </div>
            
            {/* Mode selector */}
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="w-32 bg-charcoal/50 border-white/10 text-white text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_time">All Time</SelectItem>
                <SelectItem value="last_12_months">Last 12 Months</SelectItem>
                <SelectItem value="yearly">By Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Year selector for yearly mode */}
          {mode === "yearly" && (
            <div className="mb-4">
              <Select value={selectedYear?.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-full bg-charcoal/50 border-white/10 text-white">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableYears().map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Current trip display */}
          {currentItem && (
            <div className="bg-white/5 rounded-lg p-4 mb-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-medium">{currentItem.title}</p>
                  <p className="text-white/60 text-sm">
                    {currentItem.location || currentItem.country}
                  </p>
                  {currentItem.completed_at && (
                    <p className="text-white/40 text-xs mt-1">
                      {new Date(currentItem.completed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  {currentItem.is_new_country && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 text-xs mb-1">
                      New Country!
                    </Badge>
                  )}
                  {currentItem.is_new_continent && (
                    <Badge className="bg-coral/20 text-coral text-xs">
                      New Continent!
                    </Badge>
                  )}
                  {currentItem.rank_changed && (
                    <Badge className="bg-amber-500/20 text-amber-400 text-xs flex items-center gap-1">
                      <Trophy className="w-3 h-3" />
                      Rank Up!
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Progress stats */}
              <div className="flex items-center gap-4 mt-3 text-xs text-white/60">
                <span>{currentItem.countries_so_far} countries</span>
                <span>{currentItem.continents_so_far} continents</span>
                <span>{currentItem.world_percentage}% world</span>
              </div>
            </div>
          )}
          
          {/* Timeline slider */}
          <div className="mb-4">
            <Slider
              value={[currentIndex]}
              max={(replayData?.timeline?.length || 1) - 1}
              step={1}
              onValueChange={handleSeek}
              disabled={!hasAccess}
              className="cursor-pointer"
            />
            <div className="flex justify-between text-xs text-white/40 mt-1">
              <span>Start</span>
              <span>{currentIndex + 1} / {replayData?.timeline?.length || 0}</span>
              <span>End</span>
            </div>
          </div>
          
          {/* Playback controls */}
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSkipBack}
              disabled={!hasAccess}
              className="text-white/60 hover:text-white"
            >
              <SkipBack className="w-5 h-5" />
            </Button>
            
            <Button
              size="lg"
              onClick={isPlaying ? handlePause : handlePlay}
              className={`rounded-full ${hasAccess ? 'bg-coral hover:bg-coral/90' : 'bg-white/10'}`}
            >
              {isPlaying ? (
                <Pause className="w-6 h-6" />
              ) : (
                <>
                  {!hasAccess && <Lock className="w-4 h-4 mr-2" />}
                  <Play className="w-6 h-6" />
                </>
              )}
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSkipForward}
              disabled={!hasAccess}
              className="text-white/60 hover:text-white"
            >
              <SkipForward className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Summary stats */}
          {replayData?.summary && (
            <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/10">
              <div className="text-center">
                <p className="text-xl font-bold text-white">{replayData.summary.total_trips}</p>
                <p className="text-xs text-white/60">Trips</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-coral">{replayData.summary.total_countries}</p>
                <p className="text-xs text-white/60">Countries</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-emerald-400">{replayData.summary.total_continents}</p>
                <p className="text-xs text-white/60">Continents</p>
              </div>
            </div>
          )}
          
          {/* Teaser for non-premium */}
          {!hasAccess && replayData?.teaser && (
            <div className="mt-4 p-3 bg-coral/10 rounded-lg border border-coral/20">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-coral" />
                <span className="text-sm font-medium text-white">
                  {replayData.teaser.total_trips} trips to replay
                </span>
              </div>
              <p className="text-xs text-white/60 mb-3">{replayData.teaser.message}</p>
              <Button
                onClick={() => setShowPaywall(true)}
                className="w-full bg-coral hover:bg-coral/90"
                size="sm"
              >
                Learn more on duolyst.co.uk
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {showPaywall && (
        <PaywallScreen
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          type={PaywallType.ROUTE_REPLAY}
          feature="route_replay"
          title="Route Replay"
          description="Watch your travel journey unfold on the map."
        />
      )}
    </>
  );
};

export default RouteReplay;
